import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, ease: 'easeOut' },
  },
};

const services = [
  {
    title: 'Mechanical Repairs',
    desc: 'Full mechanical repairs from minor fixes to major overhauls. Our experienced team diagnoses and resolves issues efficiently to get you back on the road. Whether you have a strange noise, a warning light, or a known fault, we will get to the bottom of it and carry out the repair to the highest standard. No job is too big or too small at Auto Doctor.',
    imageSrc: '/workshop.jpg',
    imageAlt: 'Auto Doctor workshop interior with vehicles on lifts',
    imageStyle: { height: '360px', objectFit: 'cover', objectPosition: 'center' },
  },
  {
    title: 'Warrant of Fitness',
    desc: 'Comprehensive WOF inspections carried out by certified technicians. We will let you know exactly what your vehicle needs to pass. We check all the required safety components thoroughly and give you an honest, upfront assessment before any work is carried out. Fast turnaround with no surprises.',
    imageSrc: '/exterior.jpg',
    imageAlt: 'Auto Doctor workshop exterior with signage and flags',
    imageStyle: { aspectRatio: '16/9', objectFit: 'cover', objectPosition: 'center' },
  },
  {
    title: 'Vehicle Servicing',
    desc: 'Regular servicing keeps your vehicle running at its best. We follow manufacturer guidelines and use quality parts to keep your car, ute or van in peak condition. Whether it is an oil change, filter replacement, or a full service, we make sure everything is checked and done properly so you can drive away with confidence.',
    imageSrc: '/reception.jpg',
    imageAlt: 'Auto Doctor reception desk',
    imageStyle: { aspectRatio: '16/9', objectFit: 'cover', objectPosition: 'center' },
  },
  {
    title: 'Diagnostics',
    desc: 'Modern vehicles need modern diagnostic tools. We use the latest equipment to identify issues quickly and accurately. Our diagnostic tools connect to your vehicle\'s computer systems to read fault codes, live data, and sensor information, giving us a precise picture of what is happening before we start any work. This saves you time and money.',
    imageSrc: '/diagnostics2.jpg',
    imageAlt: 'Diagnostic scanner connected to vehicle interior',
    imageStyle: { height: '360px', objectFit: 'cover', objectPosition: 'center' },
  },
  {
    title: 'Tyres',
    desc: 'Supply and fitting of quality tyres to suit all vehicles and budgets. We will help you find the right tyre for your driving needs, whether you prioritise fuel economy, performance, or all-weather grip. We stock a range of trusted brands and can advise on the best option for your vehicle. Professional fitting included.',
    imageSrc: '/tyres.jpg',
    imageAlt: 'Full tyre stock rack at Auto Doctor',
    imageStyle: { height: '360px', objectFit: 'cover', objectPosition: 'center' },
  },
  {
    title: 'Wheel Alignment',
    desc: 'Proper alignment extends tyre life and improves handling. We use a Fasep Italian alignment machine, one of the most precise systems available, to ensure your vehicle tracks straight and handles predictably. If your car is pulling to one side, wearing tyres unevenly, or has had recent suspension work, an alignment check is essential. We get it right first time.',
    imageSrc: '/alignment-car.jpg',
    imageAlt: 'Hyundai on the wheel alignment ramp at Auto Doctor',
    imageStyle: { height: '360px', objectFit: 'cover', objectPosition: 'center' },
  },
  {
    title: 'Tyre Balancing',
    desc: 'Precision tyre balancing using our Hunter SmartWeight Pro system. Balanced tyres mean a smoother ride, longer tyre life and better handling. Out-of-balance tyres cause vibration, uneven wear, and unnecessary strain on your suspension and wheel bearings. We balance every tyre accurately so you drive away with confidence.',
    imageSrc: '/balancing.jpg',
    imageAlt: 'Hunter SmartWeight Pro tyre balancing machine at Auto Doctor',
    imageStyle: { height: '360px', objectFit: 'cover', objectPosition: 'center 60%' },
  },
];

function ServiceImage({ svc }) {
  return (
    <img
      src={svc.imageSrc}
      alt={svc.imageAlt}
      className="w-full rounded-2xl"
      style={svc.imageStyle}
    />
  );
}

export default function Services() {
  return (
    <div style={{ backgroundColor: '#0A0F1C' }}>
      {/* Page header */}
      <section
        className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 text-center"
        style={{ backgroundColor: '#070C18', borderBottom: '1px solid rgba(204,0,0,0.2)' }}
      >
        <motion.div initial="hidden" animate="visible" variants={fadeUp}>
          <p className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: '#CC0000' }}>
            What We Offer
          </p>
          <h1
            className="text-5xl sm:text-6xl font-bold text-white mb-4"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            Our Services
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Full-service mechanical care for all makes and models, right here in Waikanae on the Kapiti Coast.
          </p>
        </motion.div>
      </section>

      {/* Services alternating layout */}
      {services.map((svc, i) => {
        const isEven = i % 2 === 0;
        return (
          <section
            key={i}
            style={{ backgroundColor: isEven ? '#0A0F1C' : '#0D1220' }}
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
              <div className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-12`}>

                {/* Service image(s) */}
                <motion.div
                  className="w-full lg:w-1/2 flex-shrink-0"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: '-80px' }}
                  variants={fadeUp}
                >
                  <ServiceImage svc={svc} />
                </motion.div>

                {/* Text */}
                <motion.div
                  className="w-full lg:w-1/2"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: '-80px' }}
                  variants={fadeUp}
                >
                  <p className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: '#CC0000' }}>
                    Service {String(i + 1).padStart(2, '0')}
                  </p>
                  <h2
                    className="text-4xl sm:text-5xl font-bold text-white mb-5"
                    style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                  >
                    {svc.title}
                  </h2>
                  <p className="text-gray-300 text-base sm:text-lg leading-relaxed mb-8">
                    {svc.desc}
                  </p>
                  <Link
                    to="/contact"
                    className="inline-block px-7 py-3 font-semibold text-white rounded transition-all duration-200"
                    style={{ backgroundColor: '#CC0000' }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#AA0000'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#CC0000'; }}
                  >
                    Book This Service
                  </Link>
                </motion.div>
              </div>
            </div>
          </section>
        );
      })}

      {/* CTA strip */}
      <section
        className="py-16 px-4 sm:px-6 lg:px-8 text-center"
        style={{ backgroundColor: '#070C18', borderTop: '1px solid rgba(204,0,0,0.2)' }}
      >
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
        >
          <h2
            className="text-4xl sm:text-5xl font-bold text-white mb-4"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            Ready to Book?
          </h2>
          <p className="text-gray-400 text-lg mb-8 max-w-xl mx-auto">
            Contact Auto Doctor today and let us take care of your vehicle.
          </p>
          <Link
            to="/contact"
            className="inline-block px-10 py-4 text-lg font-semibold text-white rounded transition-all duration-200"
            style={{ backgroundColor: '#CC0000' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#AA0000'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#CC0000'; }}
          >
            Get in Touch
          </Link>
        </motion.div>
      </section>
    </div>
  );
}
